import React from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, InputNumber, Typography } from '@duxton-web/ui';
import { styled } from 'styled-components';

import {
  insertCanaryPhaseBeforeFinal,
  removeEditableCanaryPhase,
} from './phase-utils';
import { CanaryPhase } from './types';

interface PhasesSectionProps {
  phases: CanaryPhase[];
  onChange: (phases: CanaryPhase[]) => void;
  disabled?: boolean;
}

const PhasesSection: React.FC<PhasesSectionProps> = ({
  phases,
  onChange,
  disabled = false,
}) => {
  const handlePhaseChange = (
    index: number,
    field: keyof CanaryPhase,
    value: number | null,
  ) => {
    if (value === null) return;
    const isFinalPercentage =
      index === phases.length - 1 && field === 'targetPercentage';
    if (isFinalPercentage) return;

    const updatedPhases = [...phases];
    updatedPhases[index] = { ...updatedPhases[index], [field]: value };
    onChange(updatedPhases);
  };

  const handleAddPhase = () => {
    onChange(insertCanaryPhaseBeforeFinal(phases));
  };

  const handleRemovePhase = (index: number) => {
    onChange(removeEditableCanaryPhase(phases, index));
  };

  return (
    <Wrapper>
      <Header>
        <Typography.Text strong>Canary Phases</Typography.Text>
        <Typography.Text type="secondary">
          Minimum 3 phases required
        </Typography.Text>
      </Header>
      <TableContainer>
        <TableHead>
          <ColPhase>Phase</ColPhase>
          <ColPercent>Percentage</ColPercent>
          <ColDuration>Duration (mins)</ColDuration>
          <ColAction />
        </TableHead>
        {phases.map((phase, index) => {
          const isFinalPhase = index === phases.length - 1;

          return (
            <TableRow key={index}>
              <ColPhase>
                <Typography.Text>{index + 1}</Typography.Text>
                {isFinalPhase && (
                  <FixedLabel data-testid="fixed-final-phase">Fixed</FixedLabel>
                )}
              </ColPhase>
              <ColPercent>
                <InputNumber
                  value={phase.targetPercentage}
                  min={1}
                  max={100}
                  step={1}
                  precision={0}
                  disabled={disabled || isFinalPhase}
                  onChange={(value) =>
                    handlePhaseChange(index, 'targetPercentage', value)
                  }
                  size="small"
                  style={{ width: '100%' }}
                  addonAfter="%"
                />
              </ColPercent>
              <ColDuration>
                {isFinalPhase ? (
                  // Retain durationMins in the model for payload compatibility;
                  // the promoted 100% release is not a timed observation phase.
                  <FinalReleaseTiming data-testid="fixed-final-phase-timing">
                    After promotion
                  </FinalReleaseTiming>
                ) : (
                  <InputNumber
                    value={phase.durationMins}
                    min={1}
                    step={1}
                    disabled={disabled}
                    onChange={(value) =>
                      handlePhaseChange(index, 'durationMins', value)
                    }
                    size="small"
                    style={{ width: '100%' }}
                  />
                )}
              </ColDuration>
              <ColAction>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined {...({} as any)} />}
                  onClick={() => handleRemovePhase(index)}
                  disabled={disabled || phases.length <= 3 || isFinalPhase}
                  title={
                    isFinalPhase
                      ? 'The final Canary phase is fixed at 100%'
                      : 'Remove phase'
                  }
                />
              </ColAction>
            </TableRow>
          );
        })}
      </TableContainer>
      <Button
        type="dashed"
        size="small"
        icon={<PlusOutlined {...({} as any)} />}
        onClick={handleAddPhase}
        disabled={disabled}
        style={{ alignSelf: 'flex-start' }}
      >
        Add Phase
      </Button>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const TableContainer = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  gap: 12px;
`;

const TableHead = styled(Row)`
  background: #fafafa;
  border-bottom: 1px solid #e8e8e8;
  font-weight: 600;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
  padding: 8px 12px;
`;

const TableRow = styled(Row)`
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const ColPhase = styled.div`
  width: 60px;
  flex-shrink: 0;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FixedLabel = styled.span`
  color: #5c5c5c;
  font-size: 10px;
  line-height: 12px;
`;

const ColPercent = styled.div`
  width: 140px;
  flex-shrink: 0;
`;

const ColDuration = styled.div`
  width: 120px;
  flex-shrink: 0;
`;

const FinalReleaseTiming = styled.div`
  width: 100%;
  color: #5c5c5c;
  font-size: 12px;
  line-height: 24px;
`;

const ColAction = styled.div`
  width: 32px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
`;

export default PhasesSection;
